import { StackScreenProps } from "@react-navigation/stack";
import { ROAM_LOCATION_HEADER_KEY } from "../../constants";
import { QUERY_PRODUCT_LIST } from "../../modules/products/utils";
import { useLoadMoreList } from "../../utilities/load-more-list";
import { Customer } from "@towersystems/roam-common/lib/generated-types";
import {
  DEFAULT_PAGE_WIDTH,
  Text,
  FlatListSearchable,
  Page,
  useWithAppState,
  Divider,
  Button,
} from "@towersystems/roam-common-native";
import { useScheduler } from "../../utilities/scheduler";
import { useSearch } from "../../utilities/search";
import React from "react";
import { useSelectableCustomersFilterContext } from "./hooks";
import { Spacings, View } from "react-native-ui-lib";
import { useTranslation } from "../../utilities/i18n";
import { FlatListSearchableSkeleton } from "../FlatListSearchableSkeleton";
import colors from "react-native-ui-lib/src/style/colors";
import { Feather } from "@expo/vector-icons";
import { CustomerResourceListItem } from "../CustomerResourceListItem";
import { QUERY_CUSTOMERS_LIST } from "../../modules/point-of-sale/utils/customer";

export type SelectableCustomersFilterScreenProps = StackScreenProps<
  any,
  "SelectableCustomersFilterScreen"
>;

export const SelectableCustomersFilterScreen = ({
  navigation,
}: SelectableCustomersFilterScreenProps) => {
  const { t } = useTranslation("common");

  const { onCustomerSelect, selectedCustomerIds, onClearAllPress } =
    useSelectableCustomersFilterContext();

  const { schedule } = useScheduler(500);
  const search = useSearch();

  React.useEffect(() => {
    navigation.setOptions({
      headerRight() {
        return (
          <Button
            label={t("common:clear_filters")}
            isAction
            disabled={selectedCustomerIds.length == 0}
            onPress={onClearAllPress}
            size="compact"
          ></Button>
        );
      },
    });
  }, [selectedCustomerIds]);

  const loadMoreList = useLoadMoreList<Customer>({
    initialLoad: false,
    query: QUERY_CUSTOMERS_LIST,
    resultKey: "customers",
    sort: { updatedAt: "DESC" },
    variableFormatter: (variables) => {
      return {
        options: variables,
      };
    },
  });

  React.useEffect(() => {
    if (search.touched) {
      schedule(() => handleLoadMore(true));
    }
  }, [search.query, search.touched]);

  const handleLoadMore = (reset?: boolean) => {
    loadMoreList.loadMore(
      {
        filter: {
          term: { contains: search.query },
        },
      },
      reset
    );
  };

  const handleOnSelect = (product: Customer) => {
    onCustomerSelect(product.id);
  };

  const renderItem = React.useCallback(
    ({ item }: { item: Customer }) => {
      return (
        <CustomerResourceListItem
          customer={item}
          action={() => handleOnSelect(item)}
          actionRightComponent={
            selectedCustomerIds.includes(item.id) ? (
              <Feather name="check" size={24} color={colors.primary} />
            ) : (
              <View />
            )
          }
        />
      );
    },
    [selectedCustomerIds]
  );

  const emptyComponentMarkup = (
    <View>
      <Text>{t("common:empty_list")}</Text>
    </View>
  );

  if (loadMoreList.loading && loadMoreList.called == false) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />;
      </Page>
    );
  }

  const items: Customer[] = loadMoreList.items || [];

  return (
    <Page scrollable={false} padding={0} fullWidth>
      <FlatListSearchable
        contentContainerStyle={{
          maxWidth: DEFAULT_PAGE_WIDTH,
          width: "100%",
          alignSelf: "center",
          paddingHorizontal: Spacings.s5,
          paddingTop: Spacings.s5,
        }}
        placeholder={t("search2")}
        onChange={(query) => search.setQuery(query)}
        value={search.query}
        ItemSeparatorComponent={Divider}
        renderItem={renderItem}
        refreshing={loadMoreList.loading}
        data={items}
        scrollEnabled={true}
        ListEmptyComponent={emptyComponentMarkup}
        onEndReached={() => handleLoadMore(false)}
        renderSearchBarHeader={(searchBarMarkup, divider) => (
          <View>
            <View>
              {searchBarMarkup}
              {divider}
            </View>
          </View>
        )}
      />
    </Page>
  );
};
